import { Injectable, NgZone } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { Chat, Contact, Group, groupMessage, Message } from "./typing";
import { Client, IMessage, StompSubscription } from "@stomp/stompjs";
import { Subject } from "rxjs";
import SockJS from "sockjs-client";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class MessagePanelService {
  private stompClient!: Client;
  private connected = false;

  private newMessageSubject = new Subject<Message>();
  private unreadStatsSubject = new BehaviorSubject<any>(null);
  private unreadPerConversationSubject = new BehaviorSubject<any>(null);
  private activeConversationSubscriptions: number[] = [];
  private subscribedConversations = new Set<number>();
  token: string | null;
  private messageEvent$ = new Subject<void>();
  constructor(
    private http: HttpClient,
    private zone: NgZone,
  ) {
    this.token = sessionStorage.getItem("token");
  }

  // Create a new message in a conversation
  createMessage(messageData: {
    toReceiverId: number;
    message: string;
    subject: string;
  }): Observable<any> {
    return this.http.post<any>(`/messagepanel/create`, messageData).pipe(tap());
  }

  replyToIndividualMessage(Message: any): Observable<any> {
    return this.http.put<any>(`/messagepanel/update`, Message);
  }

  // Create a new group chat
  createGroupMessage(Group: any): Observable<any> {
    return this.http.post<any>(`/group/create`, Group).pipe();
  }

  updateGroupMessage(Group: any): Observable<any> {
    return this.http.put<any>(`/group/update`, Group).pipe(tap());
  }

  // Retrieve all groups for the current user
  getUserGroups(): Observable<Group[]> {
    return this.http
      .get<any>(`/messagepanel/getUserGroups`)
      .pipe(map((response: any) => response.groups as Group[]));
  }

  // Add a member to an existing group
  addMemberToGroup(payload: {
    groupId: number;
    participantIds: string;
  }): Observable<any> {
    return this.http.put<any>(`/group/addparticipants`, payload).pipe(tap());
  }

  updateGroupName(payload: { id: number; groupName: string }): Observable<any> {
    return this.http.put<any>(`/group/changegroupname`, payload).pipe(tap());
  }

  getGroupMembers(id: any): Observable<any> {
    return this.http
      .get<any>(`/group/getconversationparticipants/${id}`)
      .pipe(map((response: any) => response));
  }

  getOneMessage(id: any): Observable<any> {
    return this.http
      .get<any>(`/messagepanel/get/${id}`)
      .pipe(map((response: any) => response));
  }

  getChats(): Observable<any> {
    return this.http.get<any>(`/messagepanel/getAll`).pipe(tap());
  }

  getStudentChats(): Observable<any> {
    return this.http.get<any>(`/messagepanel/getstudentmessages`).pipe(tap());
  }

  getGroups(): Observable<Group[]> {
    return this.http.get<Group[]>(`/group/getAll`).pipe(tap());
  }

  getContacts(): Observable<any[]> {
    return this.http.get<any[]>("/user/getallmessageusers").pipe(tap());
  }
  getSchoolContacts(): Observable<any[]> {
    return this.http.get<any[]>("/user/student-visible-usertypes").pipe(tap());
  }

  getMessages(conversationId: any): Observable<Message[]> {
    return this.http
      .get<Message[]>(`/messagepanel/getAll`, {
        params: { conversationId },
      })
      .pipe(tap());
  }

  getGroupMessages(conversationId: string): Observable<Message[]> {
    return this.http
      .get<Message[]>(`/group/getAll`, {
        params: { conversationId },
      })
      .pipe(tap());
  }

  fetchUnreadMessages(): Observable<any> {
    return this.http.get<any>(`/messagepanel/unread`).pipe(tap());
  }

  markMessagesAsRead(conversationId: number): Observable<any> {
    // Optimistically reset unread in UI
    this.resetUnread(conversationId);

    return this.http.put<any>(
      `/messagepanel/mark-viewed/${conversationId}`,
      {},
    );
  }

  fetchUnreadMessagesCount(): Observable<any> {
    return this.http
      .get<any>(`/messagepanel/unread-per-conversation`)
      .pipe(tap((res) => this.unreadPerConversationSubject.next(res)));
  }

  // ------------------------
  // WebSocket Setup
  // ------------------------

  connectWebSocket(userId: number) {
    if (this.connected) return;

    this.stompClient = new Client({
      // webSocketFactory: () => new SockJS(`http://localhost:9004/mednoc/ws`),
      // webSocketFactory: () => new SockJS(`https://mednocapi.lemu.co.ke/mednoc/ws`),
      // webSocketFactory: () => new SockJS(`https://api.mednoccollege.com/mednoc/ws`),

      webSocketFactory: () => new SockJS(`${environment.baseApiUrl}/mednoc/ws`),
      connectHeaders: {
        Authorization: `Basic ${this.token}`,
      },
      reconnectDelay: 1000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      debug: (str) => console.log("STOMP Debug:", str),
    });

    const startTime = Date.now();

    this.stompClient.onConnect = () => {
      const duration = Date.now() - startTime;
      // console.log(`WebSocket connected in ${duration} ms`);
      this.connected = true;

      this.stompClient.subscribe(
        `/topic/users/${userId}/unread-stats`,
        (msg) => {
          const stats = JSON.parse(msg.body);
          this.zone.run(() => {
            this.unreadStatsSubject.next(stats);
          });
        },
      );

      this.stompClient.subscribe(
        `/topic/users/${userId}/unread-per-conversation`,
        (msg) => {
          const perConv = JSON.parse(msg.body);
          this.zone.run(() => {
            this.unreadPerConversationSubject.next(perConv);
          });
        },
      );

      this.resubscribeToConversations();
    };

    this.stompClient.onDisconnect = () => {
      this.connected = false;
    };

    this.stompClient.activate();
  }

  subscribeToConversation(conversationId: number): StompSubscription | null {
    if (!this.stompClient || !this.connected) return null;

    this.subscribedConversations.add(conversationId);

    return this.stompClient.subscribe(
      `/topic/conversations/${conversationId}`,
      (msg: IMessage) => {
        const message: Message = JSON.parse(msg.body);

        this.zone.run(() => {
          this.newMessageSubject.next(message);

          this.incrementUnread(conversationId);
        });
      },
    );
  }

  private resubscribeToConversations() {
    this.subscribedConversations.forEach((convId) => {
      this.stompClient.subscribe(
        `/topic/conversations/${convId}`,
        (msg: IMessage) => {
          const message: Message = JSON.parse(msg.body);
          this.zone.run(() => {
            this.newMessageSubject.next(message);
            this.incrementUnread(convId);
          });
        },
      );
    });
  }

  private incrementUnread(conversationId: number) {
    const current = this.unreadPerConversationSubject.value || {};
    const updated = {
      ...current,
      [conversationId]: (current[conversationId] || 0) + 1,
    };
    this.unreadPerConversationSubject.next(updated);

    const globalStats = this.unreadStatsSubject.value || { total: 0 };
    this.unreadStatsSubject.next({
      ...globalStats,
      total: (globalStats.total || 0) + 1,
    });
  }

  private resetUnread(conversationId: number) {
    const current = this.unreadPerConversationSubject.value || {};
    const updated = { ...current, [conversationId]: 0 };
    this.unreadPerConversationSubject.next(updated);
  }

  onMessageEvent() {
    return this.messageEvent$.asObservable();
  }
  onNewMessage() {
    return this.newMessageSubject.asObservable();
  }

  onUnreadStats() {
    return this.unreadStatsSubject.asObservable();
  }

  onUnreadPerConversation() {
    return this.unreadPerConversationSubject.asObservable();
  }

  notifyUnreadUpdate(userId: number, conversationId: number) {
    if (!this.stompClient || !this.connected) return;

    this.stompClient.publish({
      destination: `/app/notify-unread/${userId}`,
      body: JSON.stringify({ conversationId }),
    });
    this.messageEvent$.next();
  }
}
